/**
 * useModalFocusTrap — DEC-1098 (first use), shared by EnrollmentPacketModal
 * and UnsupportedManufacturerModal.
 *
 * Audit note (DEC-1098, 2026-08-01): every existing DECO modal — Disconnect
 * ConfirmModal, WithdrawModal, MeterSelectionModal, BetaWelcomeModal,
 * TexturePermissionExplainer — closes on Escape and several restore focus to
 * whatever opened them, but NONE of them actually trap Tab: keyboard focus
 * can walk out of the dialog into the page behind the backdrop. The Design
 * System audit already flagged AuthModal specifically for this (see
 * project_design_system_audit_findings memory); it turns out to be true of
 * every modal in the app, not just AuthModal. This hook is the first real
 * fix, scoped to the two modals this pass adds. Retrofitting the other five
 * is a good follow-up (flagged in the DEC-1098 report) rather than something
 * to risk in the same pass as unrelated features.
 *
 * Usage:
 *   const dialogRef = useRef(null);
 *   const trap = useModalFocusTrap({ active: true, containerRef: dialogRef, onClose });
 *   <div className="modal" onKeyDown={trap.onKeyDown}>
 *     <div className="modal-content" ref={dialogRef} tabIndex={-1}>...
 *
 * Behavior:
 *   - On activate: remembers document.activeElement (the trigger), then
 *     moves focus to `initialFocusRef.current` when the caller passes one
 *     (the field the user actually needs next — e.g. Email when the Role
 *     arrived preselected), else the first focusable element inside the
 *     container (or the container itself, which carries tabIndex={-1} for
 *     exactly this).
 *   - Tab / Shift+Tab: cycles within the container's focusable elements —
 *     Tab on the last wraps to the first, Shift+Tab on the first wraps to
 *     the last. Real focus trapping, not just "closes on Escape".
 *   - Escape: calls onClose (skipped when closeGuard() returns true — e.g.
 *     don't let Escape abandon an in-flight mutating request).
 *   - On deactivate/unmount: returns focus to the captured trigger element,
 *     so a keyboard/screen-reader user lands exactly back where they were
 *     (WCAG 2.4.3 Focus Order) instead of at the top of the page.
 */
import { useEffect, useRef } from 'react';

// Elements this trap will cycle between. `[tabindex]:not([tabindex="-1"])`
// picks up the (rare) explicitly-focusable non-form element without
// including deliberately-unfocusable ones (tabIndex={-1} containers, e.g.
// this hook's own dialog root).
export const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/** Visible, focusable elements only. offsetParent is null for display:none —
 *  but it is ALSO null for every element in layout-less environments (jsdom),
 *  where that filter would blind the trap entirely. So: when at least one
 *  candidate has a real offsetParent we're in a layout-aware browser and use
 *  the strong check; otherwise fall back to attribute-level hiddenness. Our
 *  modals unmount hidden content rather than CSS-hiding it, so the fallback
 *  is not weaker in practice. */
function focusableElements(container) {
  if (!container) return [];
  const candidates = Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR));
  const layoutAware = candidates.some((el) => el.offsetParent !== null);
  return candidates.filter((el) =>
    layoutAware ? el.offsetParent !== null : !(el.hidden || el.closest('[hidden]'))
  );
}

export function useModalFocusTrap({ active, containerRef, onClose, closeGuard, initialFocusRef }) {
  const previousFocusRef = useRef(null);

  useEffect(() => {
    if (!active) return undefined;
    previousFocusRef.current = document.activeElement;

    // Defer to the next frame so the dialog has actually mounted/painted
    // before we try to move focus into it (mirrors BetaWelcomeModal's
    // requestAnimationFrame guard for the same reason).
    const frame = requestAnimationFrame(() => {
      const preferred = initialFocusRef?.current;
      const first = focusableElements(containerRef.current)[0];
      (preferred || first || containerRef.current)?.focus();
    });

    return () => {
      cancelAnimationFrame(frame);
      // Return focus to whatever opened this modal (the Focus trigger) —
      // WCAG 2.4.3. Guard with `?.focus?.()` in case the trigger was
      // removed from the DOM while the modal was open (e.g. its row
      // scrolled out via a refetch).
      previousFocusRef.current?.focus?.();
    };
    // initialFocusRef is a ref object (stable identity) — not a dependency.
  }, [active, containerRef]); // eslint-disable-line react-hooks/exhaustive-deps

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      if (closeGuard && closeGuard()) return;
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key !== 'Tab') return;
    const focusables = focusableElements(containerRef.current);
    if (focusables.length === 0) {
      // Nothing focusable inside (shouldn't happen — the close button alone
      // qualifies) — keep focus pinned to the container rather than letting
      // it leak to the page behind the backdrop.
      e.preventDefault();
      containerRef.current?.focus();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
    // Any other Tab (moving between two interior elements) is left alone —
    // native tab order handles it.
  };

  return { onKeyDown };
}
